/**
 * The ONLY way to build a query key — story 0.5 AC-5, and the most fragile point
 * in the whole epic.
 *
 * 🔒 **If one query forgets `idCliente`, two tenants share a cache entry and the
 * platform shows the wrong client's numbers with no error at all.** There is no
 * stack trace for that failure — a user simply reads a figure that belongs to
 * someone else. BR-19 is why the selector exists; this helper is why it works.
 *
 * The defence is that `idCliente` is the FIRST POSITIONAL ARGUMENT and cannot be
 * omitted. A key built as an inline array (`['oportunidades', filtros]`) compiles
 * happily; one built here does not exist without a tenant.
 *
 * `chaveGlobal()` is the deliberate escape hatch, for the two things that genuinely
 * are not tenant-scoped — and naming it makes those cases visible in review rather
 * than indistinguishable from a mistake.
 */

/** Anything safe to put in a key: serialisable, and stable across renders. */
export type ParteDeChave = string | number | boolean | null | undefined | Record<string, unknown>

/**
 * A tenant-scoped key. `idCliente` leads, so switching client changes the key and
 * TanStack Query treats the result as a different query — it **invalidates rather
 * than merges**, which is precisely what BR-19 demands.
 */
export function chaveDeQuery(
  idCliente: string,
  recurso: string,
  ...resto: ParteDeChave[]
): readonly unknown[] {
  if (!idCliente) {
    // Loud in dev rather than a cache entry keyed on `undefined`, which would be
    // shared by every tenant that hit the same bug.
    throw new Error(
      `chaveDeQuery("${recurso}") foi chamada sem idCliente. Toda consulta de módulo é escopada por cliente (BR-19).`,
    )
  }
  return ['cliente', idCliente, recurso, ...resto] as const
}

/**
 * A key for data that is genuinely NOT tenant-scoped.
 *
 * Only two things qualify today, and both are structural rather than convenient:
 *  - **Administração** (`/admin/*`) — it manages tenants, so it is above them;
 *  - **the compliance padrão catalog** — global by BR-23, IOX-only, no selector.
 *
 * Reach for this anywhere else and the reviewer should ask why.
 */
export function chaveGlobal(recurso: string, ...resto: ParteDeChave[]): readonly unknown[] {
  return ['global', recurso, ...resto] as const
}

/**
 * Matches every tenant-scoped key for one client — what the switch handler passes
 * to `removeQueries` so the outgoing tenant's data does not linger.
 */
export function prefixoDoCliente(idCliente: string): readonly unknown[] {
  return ['cliente', idCliente] as const
}
